import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { FormComponentProps } from '@ant-design/compatible/lib/form';
import { isNil } from 'lodash';

import { useEditUserDetailInfo } from '../hooks';
import { useEdit } from '../../../../../../hooks/common/useEdit';
import { setClearDash, setPhoneFormat, setTrim } from '../../../../../../lib/common';
import {
  ACCOUNT_USER_BANK_ID,
  ACCOUNT_USER_BANK_NAME,
  ACCOUNT_USER_DETAIL_STATUS,
  ACCOUNT_USER_GENDER_TYPE,
  ACCOUNT_USER_RECEIVE_TYPE,
} from '../../../../../../constants/service/app/accountUserConstants';
import { setDateFormat } from '../../../../../../lib/moment';
import { DATE_FORMAT } from '../../../../../../constants/service/common/utilConstants';
import { EditableUserDetailType } from '../../common/types/accountUserDetail';
import { IUserInfoEditSectionProps } from './UserInfoEditSection';
import { EditableUserDetailViewType } from './types';
import { getGenderForEdit, getUserStatusForEdit } from '../../common/utils';

export interface IUserInfoEditSectionViewModel extends FormComponentProps {
  editableUserInfo: EditableUserDetailViewType;
  adminLevel: 0 | 1 | 2;
  isEditable: boolean;
  isOpenEditAccountUserStatusModal: boolean;
  handleSave: () => void;
  handleOpenAccountUserStatusModal: () => void;
  handleEdit: () => void;
  handleCancelEdit: () => void;
  handleInputForm: <T extends keyof EditableUserDetailType>(name: T, value: EditableUserDetailType[T]) => void;
  handleCloseAccountUserStatusModal: () => void;
}

export const useUserInfoEditSectionViewModel = (props: IUserInfoEditSectionProps): IUserInfoEditSectionViewModel => {
  const { userId, form, isActive, userDetailInfo } = props;

  //관리자 메모 입력 모달 상태
  const [isOpenEditAccountUserStatusModal, setIsOpenEditAccountUserStatusModal] = useState(false);
  const openAccountUserStatusModal = () => setIsOpenEditAccountUserStatusModal(true);

  //redux store타입을 전부 선언해줄 수 없어서 임시로 any타입 사용
  const { level: adminLevel } = useSelector((state: any) => state.app.adminInfo);

  //유저 정보 편집 요청
  const { editUserDetailInfo } = useEditUserDetailInfo({ userId });

  //유저 상세 정보 편집 상태
  const { isEditable, edit: handleEdit, cancelEdit: handleCancelEdit } = useEdit(form);

  //다른 탭 클릭시 편집 상태 초기화
  //현재 탭의 경우 컴포넌트를 언마운트 시키는 방식이 아닌 display:none으로 처리하고 있어서
  //useEffect의 clean-up에서 초기화 작업 처리 불가
  useEffect(() => {
    if (!isActive) handleCancelEdit();
  }, [isActive]);

  const formatEditableValue = <T>(value: T | null = null, initialValue: T, moreFormatValue?: T) => {
    if (!isNil(value) && value !== moreFormatValue) return value;
    return isEditable ? initialValue : '-';
  };

  /**
   * 포맷팅한 유저 form 데이터
   * */
  const editableUserInfo = {
    status: formatEditableValue<EditableUserDetailType['status']>(
      userDetailInfo.status,
      ACCOUNT_USER_DETAIL_STATUS.ACTIVATE as EditableUserDetailType['status'],
    ),
    name: userDetailInfo.name,
    email: userDetailInfo.email,
    referralCode: userDetailInfo.referralCode,
    cellPhone: setPhoneFormat(userDetailInfo.cellPhone),
    gender: formatEditableValue<EditableUserDetailType['gender']>(
      userDetailInfo.gender,
      ACCOUNT_USER_GENDER_TYPE.NOT_EXISTED as EditableUserDetailType['gender'],
    ),
    birthday: userDetailInfo.birthday,
    smsReceive: formatEditableValue<EditableUserDetailType['smsReceive']>(
      userDetailInfo.smsReceive,
      ACCOUNT_USER_RECEIVE_TYPE.REJECT as EditableUserDetailType['smsReceive'],
    ),
    emailReceive: formatEditableValue<EditableUserDetailType['emailReceive']>(
      userDetailInfo.emailReceive,
      ACCOUNT_USER_RECEIVE_TYPE.REJECT as EditableUserDetailType['emailReceive'],
    ),
    bankId: formatEditableValue<EditableUserDetailType['bankId']>(
      userDetailInfo.refundExtra?.bankId,
      ACCOUNT_USER_BANK_ID.NULL,
      ACCOUNT_USER_BANK_ID.NULL,
    ),
    bankName: formatEditableValue(
      ACCOUNT_USER_BANK_NAME[userDetailInfo.refundExtra?.bankId ?? 0],
      ACCOUNT_USER_BANK_NAME[ACCOUNT_USER_BANK_ID.NULL],
    ),
    bankHolder: formatEditableValue(userDetailInfo.refundExtra?.bankHolder, ''),
    bankNumber: formatEditableValue(userDetailInfo.refundExtra?.bankNumber, ''),
  };

  /**
   * 사용자 인풋 입력시
   * - 생일 : 날짜 선택 x시, 빈 문자열로 변경
   * - 환불 계좌 은행 : 은행 없음 선택시, 환불 계좌 번호와 예금주 초기화
   * */
  const handleInputForm = <T extends keyof EditableUserDetailType>(name: T, value: EditableUserDetailType[T]) => {
    switch (name) {
      case 'birthday':
        // 빈 날짜를 골랐을 때
        if (value === '-') {
          form.setFieldsValue({ birthday: '' });
          return;
        }
        form.setFieldsValue({ birthday: value });
        break;
      case 'bankId':
        if (!value) form.setFieldsValue({ bankHolder: '', bankNumber: '' });
    }
  };

  /**
   * 편집 버튼 클릭 > 저장 버튼 클릭 클릭시
   * 1. 사용자가 입력한 input 유효성 검사
   *  - 성공 : 유효성 검사 통과시 메모를 입력하는 모달 open
   *  - 실패 : 아무 동작 x
   * */
  const handleOpenAccountUserStatusModal = () => {
    form.validateFields((err) => {
      if (err) return;

      // 유저 편집 모달 노출
      openAccountUserStatusModal();
    });
  };

  /**
   * 편집 버튼 클릭 > 저장 버튼 클릭 > 모달에서 닫기 버튼 클릭시
   * 1. 사용자가 입력한 memo를 리셋한다
   * 2. 모달상태를 false로 변경한다
   * */
  const handleCloseAccountUserStatusModal = () => {
    form.resetFields(['memo']);
    setIsOpenEditAccountUserStatusModal(false);
  };

  /**
   * 편집 버튼 클릭 > 저장 버튼 클릭 > 모달에서 저장 버튼 클릭시
   * 1. 사용자가 입력한 input 유효성 검사
   *  - 성공 : 모달을 닫기 >  편집 api 요청 > 편집 상태 false로 변경
   *  - 실패 : 유효성 검사한 인풋으로 스크롤
   * */
  const handleSave = () => {
    form.validateFieldsAndScroll({ scroll: { offsetTop: 150, offsetBottom: 150 } }, async (err, values) => {
      if (err) return;

      const { cellPhone, gender, birthday, name, status, smsReceive, emailReceive, ...rest } = values;
      handleCloseAccountUserStatusModal();
      try {
        await editUserDetailInfo({
          userId,
          cellPhone: setClearDash(cellPhone),
          gender: getGenderForEdit(gender),
          birthday: !birthday ? '' : setDateFormat(birthday, DATE_FORMAT.YMD_DASH_FORMAT),
          name: setTrim(name),
          status: getUserStatusForEdit(status),
          smsReceive: smsReceive === false ? 0 : 1,
          emailReceive: emailReceive === false ? 0 : 1,
          ...rest,
        });

        handleCancelEdit();
      } catch (e) {
        return null;
      }
    });
  };

  return {
    form,
    editableUserInfo,
    adminLevel,
    isEditable,
    isOpenEditAccountUserStatusModal,
    handleSave,
    handleOpenAccountUserStatusModal,
    handleEdit,
    handleCancelEdit,
    handleInputForm,
    handleCloseAccountUserStatusModal,
  };
};
